/**
 * Blockly workspace → Go text/template conversion script.
 *
 * Preferred over the JSON walker when a canvas snapshot is available so
 * loops, Optional RM Insertion, and live block edits show up in Generated
 * conversion script(s).
 */

import type { Block, Workspace } from "blockly/core";
import { Blockly } from "./blockly_core.ts";
import { blockToExpression } from "./expression_serialize.ts";
import {
  isDataValueBlock,
  isRmContainerBlockType,
  OPTIONAL_INPUT_PREFIX,
  RM_ATTR_INPUT_PREFIX,
  RM_SPECIALIZATION_INPUT,
} from "./blocks/rm_blocks.ts";
import { TERM_PICK_NONE, termSetById } from "../core/openehr_term_catalog.ts";
import { isGenericValueBlockType, isSchemaStructureBlock } from "./blocks/target_blocks.ts";
import { registerSchemaBlocksFromSkeleton } from "./schema_blocks.ts";
import { ensureStockBlocklyMessages } from "./stock_messages.ts";
import { DEFAULTS_BLOCK_TYPE } from "../core/defaults/extract.ts";
import {
  CONVERSION_START_TYPE,
  findInstanceRootUnderStart,
  TEXT_DOCUMENT_BLOCK_TYPE,
} from "./instance_root.ts";
import { attributesFor, isPrimitiveRmType } from "../core/rm_meta.ts";
import type { MappingModel } from "../types/mod.ts";
import {
  createGoEmitContext,
  emitGoExpressionTemplate,
  emitGoExpr,
  emitGoFunctionDefines,
  goQuote,
  jsonPathToGoIndex,
  type GoEmitContext,
} from "../core/codegen/go_template.ts";
import { parseExpression } from "../core/expression/mod.ts";
import { registerExportTargetAdapter } from "../core/codegen/mod.ts";
import { archetypeRootIdBySlot } from "../core/openehr/rm_archetype_node_id.ts";
import { generateGoTemplate } from "../core/codegen/go_template.ts";
import { runWithoutBlocklyEvents } from "./blockly_events.ts";
import { sourcePathFromLoopList } from "./loop_block.ts";
import { INCLUDE_WHEN_BLOCK } from "./blocks/include_when.ts";
import { namedLoopBinding } from "../core/named_loop_path.ts";
import { isRelativeAuthoringPath } from "../core/mapping_model/loops.ts";
import { migrateForEachSourceState } from "./migrate_for_each_source.ts";
import {
  injectXmlnsOnOpenTag,
  wrapXmlCdata,
  xmlDeclarationLine,
  XML_ATTRIBUTES_INPUT,
  XML_CHILDREN_INPUT,
  XML_DOCUMENT_TYPE,
  XML_ELEMENT_TYPE,
  XML_ROOT_INPUT,
  XML_TEXT_INPUT,
  type XmlNamespaceDecl,
} from "../core/xml_shape.ts";

const STATEMENT_INPUT_TYPE = 3;

export function registerGoTemplateExportAdapter(): void {
  registerExportTargetAdapter({
    id: "go-template",
    extension: "tmpl",
    mime: "text/x-go-template",
    generate(model, options) {
      if (options?.blocklyState) {
        const fromCanvas = generateGoTemplateFromBlocklyState(
          options.blocklyState,
          model,
          options.skeleton,
        );
        if (fromCanvas) return fromCanvas;
      }
      return generateGoTemplate(model, options);
    },
  });
}

export function generateGoTemplateFromBlocklyState(
  state: unknown,
  model: MappingModel,
  skeleton?: import("../types/mod.ts").SkeletonNode[],
): string | null {
  if (!state || typeof state !== "object") return null;
  const workspace = new Blockly.Workspace();
  try {
    const snapshot = migrateForEachSourceState(
      JSON.parse(JSON.stringify(state)) as Record<string, unknown>,
    );
    let generated: string | null = null;
    runWithoutBlocklyEvents(() => {
      ensureStockBlocklyMessages();
      if (skeleton?.length) registerSchemaBlocksFromSkeleton(skeleton);
      Blockly.serialization.workspaces.load(snapshot, workspace);
      generated = generateGoTemplateFromWorkspace(workspace, model, skeleton);
    });
    return generated;
  } catch (err) {
    console.warn("Go template codegen from Blockly failed; falling back to JSON walker", err);
    return null;
  } finally {
    workspace.dispose();
  }
}

export function generateGoTemplateFromWorkspace(
  workspace: Workspace,
  model: MappingModel,
  skeleton?: import("../types/mod.ts").SkeletonNode[],
): string | null {
  const ctx = createGoEmitContext(undefined, model.functions);
  if (skeleton?.length) ctx.archetypeRootIds = archetypeRootIdBySlot(skeleton);
  const instanceRoot = findInstanceRootUnderStart(workspace);
  const roots = workspace.getTopBlocks(true).filter((block) =>
    block.type !== DEFAULTS_BLOCK_TYPE &&
    block.type !== "default_context_map" &&
    block.type !== "maps_create_with" &&
    block.type !== CONVERSION_START_TYPE &&
    block.type !== "procedures_defreturn" &&
    block.type !== "procedures_defnoreturn"
  );

  const lines: string[] = [
    `{{- /* Generated by intEHRgrator — Go text/template Conversion Script */ -}}`,
    `{{- /* Target: ${model.templateId || "free-form"} */ -}}`,
  ];
  const defines = emitGoFunctionDefines(model, ctx);
  if (defines) lines.push(defines);

  const rawGoSnippets = workspace.getTopBlocks(true).filter((block) =>
    (block.type === "text" || block.type === "text_code") &&
    block !== instanceRoot
  );
  for (const snippet of rawGoSnippets) {
    lines.push(...emitBlock(snippet, ctx, 0));
  }

  let body: string[] | null = null;
  if (instanceRoot?.type === TEXT_DOCUMENT_BLOCK_TYPE) {
    const value = instanceRoot.getInputTargetBlock("VALUE");
    body = value ? emitBlock(value, ctx, 0) : ['""'];
  } else if (instanceRoot) {
    body = emitBlock(instanceRoot, ctx, 0);
  } else {
    const generic = roots.find((block) =>
      isSchemaStructureBlock(block) || block.type === "json_object" ||
      block.type === XML_ELEMENT_TYPE || block.type === XML_DOCUMENT_TYPE ||
      block.type === "composition" || isRmContainerBlockType(block.type)
    );
    if (generic) {
      body = emitBlock(generic, ctx, 0);
    } else if (roots.length) {
      body = [];
      for (const root of roots) {
        if (root.type === "text" || root.type === "text_code") continue;
        body.push(...emitBlock(root, ctx, 0));
      }
    }
  }

  if (!body?.length && !rawGoSnippets.length) return null;
  if (body?.length) lines.push(...body);
  return lines.join("\n");
}

const RAW_TEXT_BLOCK_TYPES = new Set(["text", "text_code"]);

function emitBlock(block: Block, ctx: GoEmitContext, indent: number): string[] {
  if (block.type === "term_pick") return [emitTermPickJson(block)];
  if (!RAW_TEXT_BLOCK_TYPES.has(block.type)) {
    const fromExpr = emitExpressionBlock(block, ctx);
    if (fromExpr !== null) return fromExpr;
  }

  if (block.type === "for_each_list") return emitForEachList(block, ctx);
  if (block.type === INCLUDE_WHEN_BLOCK) return emitIncludeWhen(block, ctx);
  if (block.type === "controls_if") return emitControlsIf(block, ctx);
  if (block.type === "variables_set") return [];
  if (
    block.type === "defaults_block" ||
    block.type === "default_context_map" ||
    block.type === "maps_create_with"
  ) return [];

  if (block.type === "xml_text" || block.type === "xml_cdata") {
    return emitXmlTextishLive(block, ctx, indent);
  }
  if (block.type === XML_DOCUMENT_TYPE) {
    return emitXmlDocumentLive(block, ctx);
  }
  if (block.type === "text_document") {
    const value = block.getInputTargetBlock("VALUE");
    return value ? emitBlock(value, ctx, indent) : [];
  }
  if (block.type === "text" || block.type === "text_code") {
    return [String(block.getFieldValue("TEXT") ?? "")];
  }
  if (block.type === "text_handlebars") {
    return emitExpressionBlock(block, ctx) ?? [];
  }

  if (block.type === "element" || isRmContainerBlockType(block.type)) {
    return emitRmAsJson(block, ctx, indent);
  }
  if (isDataValueBlock(block)) {
    return emitDvAsJson(block, ctx, indent);
  }
  if (
    block.type === XML_ELEMENT_TYPE ||
    block.type === XML_DOCUMENT_TYPE ||
    block.type.startsWith("schema_")
  ) {
    return emitXmlOrSchema(block, ctx);
  }
  if (isGenericValueBlockType(block.type) || block.type === "target_structure") {
    return emitGeneric(block, ctx, indent);
  }
  if (block.type === "json_object" || block.type === "json_array") {
    return emitJson(block, ctx, indent);
  }

  return [`{{- /* unsupported block: ${block.type} */ -}}`];
}

/** Literal hatch text (Go snippets) must not go through JSON.stringify + goQuote. */
function emitLiveValue(block: Block, ctx: GoEmitContext, indent: number): string {
  if (RAW_TEXT_BLOCK_TYPES.has(block.type)) {
    return String(block.getFieldValue("TEXT") ?? "");
  }
  if (block.type === "math_number") {
    return String(block.getFieldValue("NUM") ?? "0");
  }
  if (block.type === "logic_boolean") {
    return block.getFieldValue("BOOL") === "TRUE" ? "true" : "false";
  }
  return (emitExpressionBlock(block, ctx) ?? emitBlock(block, ctx, indent)).join("");
}

function emitExpressionBlock(block: Block, ctx: GoEmitContext): string[] | null {
  const serialized = blockToExpression(block);
  if (!serialized) return null;
  return [emitGoExpressionTemplate(parseExpression(serialized), ctx)];
}

function goLoopIdent(name: string): string {
  return /^[A-Za-z_][A-Za-z0-9_]*$/.test(name) ? name : "item";
}

function emitForEachList(block: Block, ctx: GoEmitContext): string[] {
  const varName = String(block.getFieldValue("VAR") || "item");
  const ident = goLoopIdent(varName);
  const sourcePath = sourcePathFromLoopList(block);
  const rangeExpr = sourcePath
    ? loopRangeExpr(sourcePath, ctx)
    : (() => {
      const list = block.getInputTargetBlock("LIST");
      const listParts = list ? emitExpressionBlock(list, ctx) ?? emitBlock(list, ctx, 0) : ['""'];
      return listParts.join("").replace(/^\{\{\s*/, "").replace(/\s*\}\}$/, "") || `index .Data ""`;
    })();
  const body = block.getInputTargetBlock("DO");
  const lines = [`{{- range $${ident} := ${rangeExpr} }}`];
  const innerCtx: GoEmitContext = {
    ...ctx,
    loopVar: ident,
    loopBindings: { ...ctx.loopBindings, [varName]: `$${ident}` },
  };
  if (body) lines.push(...emitStatementChain(body, innerCtx, 0));
  lines.push(`{{- end }}`);
  return lines;
}

function loopRangeExpr(path: string, ctx: GoEmitContext): string {
  const trimmed = path.trim();
  const named = namedLoopBinding(trimmed, ctx.loopBindings);
  if (named) {
    if (named.rest === ".") return named.ident;
    let expr = named.ident;
    for (const seg of named.rest.split(".").filter(Boolean)) {
      expr = `index ${expr} ${goQuote(seg)}`;
    }
    return expr;
  }
  if (trimmed.startsWith("$.")) return jsonPathToGoIndex(trimmed);
  if (ctx.loopVar && isRelativeAuthoringPath(trimmed)) return `index . ${goQuote(trimmed)}`;
  return `index .Data ${goQuote(trimmed)}`;
}

function emitIncludeWhen(block: Block, ctx: GoEmitContext): string[] {
  const cond = block.getInputTargetBlock("WHEN");
  const condStr = cond ? emitCondition(cond, ctx) : "false";
  const lines = [`{{- if ${condStr} }}`];
  const body = block.getInputTargetBlock("DO");
  if (body) lines.push(...emitStatementChain(body, ctx, 0));
  const elseBody = block.getInputTargetBlock("ELSE");
  if (elseBody) {
    lines.push(`{{- else }}`);
    lines.push(...emitStatementChain(elseBody, ctx, 0));
  }
  lines.push(`{{- end }}`);
  return lines;
}

function emitControlsIf(block: Block, ctx: GoEmitContext): string[] {
  const lines: string[] = [];
  const { ifCount, hasElse } = ifBranchCount(block);

  for (let i = 0; i < ifCount; i++) {
    const cond = block.getInputTargetBlock(`IF${i}`);
    const body = block.getInputTargetBlock(`DO${i}`);
    const keyword = i === 0 ? "if" : "else if";
    const condStr = cond ? emitCondition(cond, ctx) : "true";
    lines.push(`{{- ${keyword} ${condStr} }}`);
    if (body) lines.push(...emitStatementChain(body, ctx, 0));
  }

  if (hasElse) {
    const elseBody = block.getInputTargetBlock("ELSE");
    lines.push(`{{- else }}`);
    if (elseBody) lines.push(...emitStatementChain(elseBody, ctx, 0));
  }

  lines.push(`{{- end }}`);
  return lines;
}

function ifBranchCount(block: Block): { ifCount: number; hasElse: boolean } {
  const saved = extraStateOf(block);
  if (saved.elseIfCount != null || saved.hasElse != null) {
    return {
      ifCount: (saved.elseIfCount ?? 0) + 1,
      hasElse: saved.hasElse ?? false,
    };
  }
  let ifCount = 0;
  while (block.getInput(`IF${ifCount}`) || block.getInput(`DO${ifCount}`)) ifCount++;
  return {
    ifCount: Math.max(ifCount, 1),
    hasElse: Boolean(block.getInput("ELSE")),
  };
}

function emitCondition(block: Block, ctx: GoEmitContext): string {
  const serialized = blockToExpression(block);
  if (serialized) return emitGoExpr(parseExpression(serialized), ctx);
  return "true";
}

function emitStatementChain(block: Block, ctx: GoEmitContext, indent: number): string[] {
  const lines: string[] = [];
  let current: Block | null = block;
  while (current) {
    lines.push(...emitBlock(current, ctx, indent));
    current = current.getNextBlock();
  }
  return lines;
}

function emitGeneric(block: Block, ctx: GoEmitContext, indent: number): string[] {
  if (block.type === "json_null") return ["null"];
  if (block.type === "json_boolean") {
    const value = block.getInputTargetBlock("VALUE");
    return value ? emitBlock(value, ctx, indent) : ["false"];
  }
  if (isGenericValueBlockType(block.type)) {
    const value = block.getInputTargetBlock("VALUE");
    return value ? emitBlock(value, ctx, indent) : [];
  }
  if (
    block.type === XML_ELEMENT_TYPE || block.type.startsWith("schema_") ||
    block.type === XML_DOCUMENT_TYPE
  ) {
    return emitXmlOrSchema(block, ctx);
  }
  if (block.type === "json_object" || block.type === "json_array") {
    return emitJson(block, ctx, indent);
  }

  const props: string[] = [];
  for (const input of block.inputList) {
    if (!input.name.startsWith("TARGET_") && !input.name.startsWith("SCHEMA_OPT_")) continue;
    const attr = input.name.replace(/^TARGET_|^SCHEMA_OPT_/, "");
    if (input.type === STATEMENT_INPUT_TYPE) {
      const chain = emitStatementList(block.getInputTargetBlock(input.name), ctx, indent);
      if (chain) props.push(`"${attr}": ${chain}`);
      continue;
    }
    const child = block.getInputTargetBlock(input.name);
    if (!child) continue;
    const rendered = emitBlock(child, ctx, indent + 1);
    if (rendered.length) props.push(`"${attr}": ${rendered.join("")}`);
  }
  if (!props.length) return [];
  return [`{${props.join(", ")}}`];
}

function emitStatementList(
  first: Block | null,
  ctx: GoEmitContext,
  indent: number,
): string | null {
  if (!first) return null;
  const parts: string[] = [];
  let current: Block | null = first;
  while (current) {
    const rendered = emitBlock(current, ctx, indent);
    if (rendered.length) parts.push(rendered.join(""));
    current = current.getNextBlock();
  }
  if (!parts.length) return null;
  if (parts.length === 1) return parts[0]!;
  return `[${parts.join(", ")}]`;
}

function emitJson(block: Block, ctx: GoEmitContext, indent: number): string[] {
  if (block.type === "json_array") {
    const count = Number((block as Block & { itemCount_?: number }).itemCount_ ?? 0);
    const items: string[] = [];
    for (let i = 0; i < count; i++) {
      const child = block.getInputTargetBlock(`ADD${i}`);
      if (child) items.push((emitBlock(child, ctx, indent + 1)).join(""));
    }
    return [`[${items.join(", ")}]`];
  }
  const props: string[] = [];
  for (const input of block.inputList) {
    if (!input.name.startsWith("TARGET_")) continue;
    const attr = input.name.slice("TARGET_".length);
    const child = block.getInputTargetBlock(input.name);
    if (!child) continue;
    props.push(`"${attr}": ${emitBlock(child, ctx, indent + 1).join("")}`);
  }
  return [`{${props.join(", ")}}`];
}

function emitRmAsJson(block: Block, ctx: GoEmitContext, indent: number): string[] {
  const props: string[] = [];
  const rmType = rmTypeOf(block);
  const slotId = String(block.getFieldValue("SLOT_ID") ?? "").trim();
  const nodeId = ctx.archetypeRootIds?.get(slotId) ||
    String(block.getFieldValue("ARCHETYPE_NODE_ID") ?? "").trim();
  if (nodeId) props.push(`"_type": ${goQuote(rmType)}, "archetype_node_id": ${goQuote(nodeId)}`);
  else props.push(`"_type": ${goQuote(rmType)}`);

  if (block.type === "element") {
    const value = block.getInputTargetBlock("VALUE");
    if (value && !value.isShadow()) {
      props.push(`"value": ${emitBlock(value, ctx, indent + 1).join("")}`);
    }
  }

  for (const input of block.inputList) {
    const attr = input.name.startsWith(RM_ATTR_INPUT_PREFIX)
      ? input.name.slice(RM_ATTR_INPUT_PREFIX.length)
      : input.name.startsWith(OPTIONAL_INPUT_PREFIX)
      ? input.name.slice(OPTIONAL_INPUT_PREFIX.length)
      : "";
    if (!attr) continue;
    if (input.type === STATEMENT_INPUT_TYPE) {
      const list = emitStatementList(block.getInputTargetBlock(input.name), ctx, indent);
      if (list) props.push(`"${attr}": ${list}`);
      continue;
    }
    const child = block.getInputTargetBlock(input.name);
    if (!child || child.isShadow()) continue;
    props.push(`"${attr}": ${emitBlock(child, ctx, indent + 1).join("")}`);
  }

  if (block.type === "party_proxy") {
    const kind = block.getInputTargetBlock(RM_SPECIALIZATION_INPUT);
    if (kind) return emitRmAsJson(kind, ctx, indent);
  }

  return [`{${props.join(", ")}}`];
}

function emitTermPickJson(block: Block): string {
  const set = termSetById(block.getFieldValue("SET"));
  const rawCode = String(block.getFieldValue("CODE") ?? "");
  const code = rawCode === TERM_PICK_NONE ? "" : rawCode;
  if (!code) return "null";
  const terminology = set?.terminologyId ?? "openehr";
  const rubric = set?.codes.find((item) => item.code === code)?.rubric ?? code;
  if (set?.valueRmType === "DV_CODED_TEXT") {
    return JSON.stringify({
      _type: "DV_CODED_TEXT",
      value: rubric,
      defining_code: {
        _type: "CODE_PHRASE",
        terminology_id: { _type: "TERMINOLOGY_ID", value: terminology },
        code_string: code,
      },
    });
  }
  return JSON.stringify({
    _type: "CODE_PHRASE",
    terminology_id: { _type: "TERMINOLOGY_ID", value: terminology },
    code_string: code,
  });
}

function emitDvAsJson(block: Block, ctx: GoEmitContext, indent: number): string[] {
  const rmType = String(block.getFieldValue("RM_TYPE") || block.type.replace(/-/g, "_")).toUpperCase();
  const props: string[] = [`"_type": ${goQuote(rmType)}`];
  for (const attr of attributesFor(rmType)) {
    if (!isPrimitiveRmType(attr.typeName) && attr.typeName !== "CODE_PHRASE") continue;
    const inputName = block.getInput(`FLD_${attr.name}`)
      ? `FLD_${attr.name}`
      : block.getInput(`OPTFLD_${attr.name}`)
      ? `OPTFLD_${attr.name}`
      : null;
    if (!inputName) continue;
    const child = block.getInputTargetBlock(inputName);
    if (!child || child.isShadow()) continue;
    props.push(`"${attr.name}": ${emitBlock(child, ctx, indent + 1).join("")}`);
  }
  return [`{${props.join(", ")}}`];
}

function emitXmlTextishLive(block: Block, ctx: GoEmitContext, indent: number): string[] {
  const value = block.getInputTargetBlock(XML_TEXT_INPUT) ?? block.getInputTargetBlock("VALUE");
  const rendered = value
    ? emitLiveValue(value, ctx, indent)
    : String(block.getFieldValue("TEXT") ?? "");
  if (block.type === "xml_cdata") return [wrapXmlCdata(rendered)];
  return rendered ? [rendered] : [];
}

function emitXmlDocumentLive(block: Block, ctx: GoEmitContext): string[] {
  const extra = extraStateOf(block);
  const extras = extra.extras ?? block.xmlDocExtras_ ?? [];
  const declaration = extra.declaration !== false &&
    (extras.length === 0 || extras.includes("declaration") || extra.declaration === true ||
      Boolean(block.getField("VERSION")));
  const lines: string[] = [];
  if (declaration) {
    lines.push(xmlDeclarationLine({
      version: String(block.getFieldValue("VERSION") || extra.version || "1.0"),
      encoding: String(block.getFieldValue("ENCODING") || extra.encoding || "UTF-8"),
      standalone: String(block.getFieldValue("STANDALONE") || extra.standalone || ""),
    }));
  }
  const root = block.getInputTargetBlock(XML_ROOT_INPUT);
  if (!root) return lines;
  const emitted = emitBlock(root, ctx, 0);
  const namespaces = namespacesFromLiveDocument(block, extra);
  if (namespaces.length && emitted[0]) {
    emitted[0] = injectXmlnsOnOpenTag(emitted[0], namespaces);
  }
  lines.push(...emitted);
  return lines;
}

function extraStateOf(block: Block): {
  xmlAttributes?: string[];
  fields?: Array<{ name: string; xmlKind?: string }>;
  declaration?: boolean;
  version?: string;
  encoding?: string;
  standalone?: string;
  namespaces?: XmlNamespaceDecl[];
  extras?: string[];
  elseIfCount?: number;
  hasElse?: boolean;
} {
  const live = block as Block & {
    extraState_?: {
      xmlAttributes?: string[];
      fields?: Array<{ name: string; xmlKind?: string }>;
      declaration?: boolean;
      version?: string;
      encoding?: string;
      standalone?: string;
      namespaces?: XmlNamespaceDecl[];
      extras?: string[];
      elseIfCount?: number;
      hasElse?: boolean;
    };
    schemaXmlAttributes_?: string[];
    schemaFields_?: Array<{ name: string; xmlKind?: string }>;
    saveExtraState?: () => unknown;
    elseifCount_?: number;
    elseCount_?: number;
  };
  const saved = live.saveExtraState?.();
  const fromSave = saved && typeof saved === "object" ? saved as {
    xmlAttributes?: string[];
    fields?: Array<{ name: string; xmlKind?: string }>;
    declaration?: boolean;
    version?: string;
    encoding?: string;
    standalone?: string;
    namespaces?: XmlNamespaceDecl[];
    extras?: string[];
    elseIfCount?: number;
    hasElse?: boolean;
  } : {};
  return {
    ...live.extraState_,
    ...fromSave,
    xmlAttributes: fromSave.xmlAttributes ??
      live.schemaXmlAttributes_ ??
      live.extraState_?.xmlAttributes,
    fields: fromSave.fields ?? live.schemaFields_ ?? live.extraState_?.fields,
    elseIfCount: fromSave.elseIfCount ?? live.elseifCount_ ?? live.extraState_?.elseIfCount,
    hasElse: fromSave.hasElse ??
      (live.elseCount_ != null ? live.elseCount_ > 0 : live.extraState_?.hasElse),
  };
}

function namespacesFromLiveDocument(
  block: Block,
  extra: { namespaces?: XmlNamespaceDecl[] },
): XmlNamespaceDecl[] {
  if (block.xmlNamespaces_?.length) return block.xmlNamespaces_;
  if (extra.namespaces?.length) return extra.namespaces;
  const out: XmlNamespaceDecl[] = [];
  let i = 0;
  while (block.getField(`PREFIX_ns${i}`) || block.getField(`URI_ns${i}`)) {
    out.push({
      prefix: String(block.getFieldValue(`PREFIX_ns${i}`) ?? ""),
      uri: String(block.getFieldValue(`URI_ns${i}`) ?? ""),
    });
    i++;
  }
  return out;
}

function emitXmlAttributeLive(block: Block, ctx: GoEmitContext): string {
  const name = String(block.getFieldValue("NAME") ?? "").trim();
  if (!name) return "";
  const val = block.getInputTargetBlock(XML_TEXT_INPUT) ?? block.getInputTargetBlock("VALUE");
  const rendered = val ? emitLiveValue(val, ctx, 0) : "";
  return ` ${name}="${rendered}"`;
}

function emitXmlOrSchema(block: Block, ctx: GoEmitContext): string[] {
  if (block.type === XML_DOCUMENT_TYPE) return emitXmlDocumentLive(block, ctx);
  const tag = xmlTagName(block);
  const extra = extraStateOf(block);
  const xmlAttributes = new Set<string>();
  for (const name of extra.xmlAttributes ?? []) xmlAttributes.add(name);
  for (const field of extra.fields ?? []) {
    if (field.xmlKind === "attribute") xmlAttributes.add(field.name);
  }

  const attrParts: string[] = [];
  const inner: string[] = [];

  if (block.type === XML_ELEMENT_TYPE) {
    let attr = block.getInputTargetBlock(XML_ATTRIBUTES_INPUT);
    while (attr) {
      if (attr.type === "xml_attribute") attrParts.push(emitXmlAttributeLive(attr, ctx));
      attr = attr.getNextBlock();
    }
    const text = block.getInputTargetBlock(XML_TEXT_INPUT);
    if (text) inner.push(emitLiveValue(text, ctx, 0));
    let current: Block | null = block.getInputTargetBlock(XML_CHILDREN_INPUT);
    while (current) {
      inner.push(...emitBlock(current, ctx, 0));
      current = current.getNextBlock();
    }
  } else {
    for (const input of block.inputList) {
      if (!input.name.startsWith("TARGET_") && !input.name.startsWith("SCHEMA_OPT_")) continue;
      const fieldName = input.name.replace(/^TARGET_|^SCHEMA_OPT_/, "");
      const child = block.getInputTargetBlock(input.name);
      if (!child) continue;
      if (xmlAttributes.has(fieldName) || child.type === "xml_attribute") {
        attrParts.push(` ${fieldName}="${emitLiveValue(child, ctx, 0)}"`);
        continue;
      }
      if (input.type === STATEMENT_INPUT_TYPE || isStructureBlock(child.type)) {
        inner.push(...emitStatementChain(child, ctx, 0));
        continue;
      }
      inner.push(`<${xmlName(fieldName)}>${emitLiveValue(child, ctx, 0)}</${xmlName(fieldName)}>`);
    }
  }

  const attrs = attrParts.join("");
  if (inner.length) return [`<${tag}${attrs}>`, ...inner, `</${tag}>`];
  return [`<${tag}${attrs}></${tag}>`];
}

function isStructureBlock(type: string): boolean {
  return type === XML_ELEMENT_TYPE || type.startsWith("schema_") || type === "controls_if" ||
    type === "include_when" || type === "for_each_list";
}

function xmlTagName(block: Block): string {
  const slot = String(block.getFieldValue("SLOT_ID") ?? "");
  const path = slot.includes(":") ? slot.slice(slot.indexOf(":") + 1) : slot;
  const fromPath = path.split("/").filter((part) => part && !part.startsWith("@")).pop();
  if (fromPath) return fromPath;
  return String(block.getFieldValue("NAME") ?? "element").trim() || "element";
}

function xmlName(value: string): string {
  const safe = value.replace(/[^A-Za-z0-9_.-]/g, "_");
  return /^[A-Za-z_]/.test(safe) ? safe : `_${safe}`;
}

function rmTypeOf(block: Block): string {
  if (block.type === "element") return "ELEMENT";
  const fromType = block.type.replace(/-/g, "_").toUpperCase();
  const field = String(block.getFieldValue("RM_TYPE") ?? "").trim();
  return field ? field.toUpperCase() : fromType;
}
